import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheckIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  HeartIcon,
  LightBulbIcon,
  RocketLaunchIcon
} from "@heroicons/react/24/outline";

const AboutPage = () => {
  const values = [
    {
      icon: LightBulbIcon,
      title: "Innovation",
      description: "Pioneering AI-driven solutions that transform how organizations approach compliance and governance.",
      gradient: "from-yellow-500 to-orange-500"
    },
    {
      icon: ShieldCheckIcon,
      title: "Security First",
      description: "Building trust through enterprise-grade security, privacy protection, and regulatory compliance.",
      gradient: "from-brand to-brand-dark"
    },
    {
      icon: HeartIcon,
      title: "Customer Success",
      description: "Dedicated to empowering our clients' success through exceptional support and continuous improvement.",
      gradient: "from-red-500 to-pink-500"
    },
    {
      icon: RocketLaunchIcon,
      title: "Excellence",
      description: "Committed to delivering world-class solutions that exceed expectations and drive measurable results.",
      gradient: "from-purple-500 to-violet-500"
    }
  ];

  const partners = [
    { name: "Auriseg", logo: "https://tanikkai.com/static/media/Auriseg.03e0c7871ab44c0da4d2.png" },
    { name: "Kirshi", logo: "https://tanikkai.com/static/media/Kirshi.7c3a39c26a0ca255d263.png" },
    { name: "Paralok", logo: "https://tanikkai.com/static/media/Paralok.3f747d1c011c03e6c2a1.png" },
    { name: "Peaq Labs", logo: "https://tanikkai.com/static/media/Peaq_labs.ca8f4a043fe67826da24.png" },
    { name: "Reach ISO", logo: "https://tanikkai.com/static/media/Reach_ISO.975149efe862bd76e58a.png" },
    { name: "Infocom IT", logo: "https://tanikkai.com/static/media/Infocom_IT.cc90322604c34571bcdd.png" }
  ];

  const testimonials = [
    {
      name: "Srinivasan Mahalingam",
      role: "Group CISO and DPO",
      company: "ANSR Inc",
      image: "https://tanikkai.com/static/media/Srinivasan.a5180122652edc8d39df.jpg",
      testimonial: "Tanikkai's AI-driven compliance automation solution has significantly simplified our compliance and regulatory processes, offering a cost-effective approach. Its robust Third-Party Risk Management (TPRM) capabilities with various options have streamlined our workflows, making it easy to address RFPs and SOWs."
    },
    {
      name: "Vikram Asokan",
      role: "Fractional Director",
      company: "Infocom IT HK ltd",
      image: "https://tanikkai.com/static/media/Vikaram_Ashokan.31b731a5cfe5a778508f.jpg",
      testimonial: "Tanikkai is an exceptional cybersecurity GRC audit tool that streamlines compliance and risk management processes. Its user-friendly interface and robust features make navigating complex regulations a breeze."
    },
    {
      name: "Sivakumar Gingee",
      role: "CISO",
      company: "Peaq Labs",
      image: "https://tanikkai.com/static/media/Sivakumar_Gingee.cc8bde264ec5a6e21174.jpg",
      testimonial: "Tanikkai takes compliance to a whole other level. It brings multiple regulatory needs into one umbrella and makes cloud deployment compliant with multiple regulatory requirements. It's a brilliant attempt at automating compliance."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative px-6 pt-12 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full border border-white/20 mb-6">
              <BuildingOfficeIcon className="w-5 h-5 text-cyan-400 mr-2" />
              <span className="text-sm font-medium text-cyan-200">About HabileSec's Tanikkai</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Empowering Global Businesses with
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent block">
                AI-Powered Compliance
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed">
              HabileSec's Tanikkai provides cutting-edge AI automation solutions for compliance and privacy management. 
              We streamline regulatory adherence, enhance data protection, and optimize risk management processes.
            </p>

            <div className="flex items-center justify-center">
              <img 
                src="https://images.unsplash.com/photo-1660165458059-57cfb6cc87e5" 
                alt="AI-Powered Compliance Platform"
                className="max-w-2xl w-full h-auto rounded-2xl shadow-2xl border border-white/20"
              />
            </div>
          </motion.div>
        </div>

        {/* Background Elements */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="relative px-6 py-20 bg-gradient-to-r from-slate-900/50 to-blue-900/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/20"
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mr-4">
                  <RocketLaunchIcon className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-white">Our Mission</h2>
              </div>
              <p className="text-gray-300 text-lg leading-relaxed">
                To revolutionize compliance management through innovative AI automation, making regulatory adherence 
                accessible, efficient, and intelligent for businesses of all sizes across the globe.
              </p>
            </motion.div>

            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/20"
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mr-4">
                  <GlobeAltIcon className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-white">Our Vision</h2>
              </div>
              <p className="text-gray-300 text-lg leading-relaxed">
                Empowering businesses globally with AI automation for Compliance, Privacy, and Security - 
                creating a world where regulatory compliance enhances rather than hinders business innovation.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="relative px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Our Core Values</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              The principles that guide everything we do and drive our commitment to excellence
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="group bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:border-white/40 transition-all duration-300 text-center"
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${value.gradient} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-300 transition-colors">
                  {value.title}
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="relative px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Our Partners</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We value our partnerships with industry-leading organizations who trust our platform
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {partners.map((partner, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:border-white/40 transition-all duration-300 flex items-center justify-center"
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="max-w-full h-12 object-contain filter brightness-0 invert opacity-70 hover:opacity-100 transition-opacity"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative px-6 py-20 bg-gradient-to-r from-slate-900/50 to-blue-900/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">What Our Customers Say</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Hear from industry leaders who are transforming their compliance operations with Tanikkai
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:border-white/40 transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="text-white font-bold text-sm">{testimonial.name}</h4>
                    <p className="text-cyan-400 text-xs">{testimonial.role}</p>
                    <p className="text-gray-400 text-xs">{testimonial.company}</p>
                  </div>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed italic">
                  "{testimonial.testimonial}"
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;